// Typed API client for every endpoint in ARCHITECTURE §6 and server/README.md "API" (admin calls in adminApi).
// Base URL: VITE_API_BASE (default: same origin). Headers: Authorization (Bearer token), X-TZ, X-Client.
// Errors become ApiError { code, message, status }. The transport is swappable (the mock backend replaces it).
import type { AppState, CardEdit, ChatEvent, DoneFrom, LaterWhen, RegisterRequest, StuckAction, StuckReason } from '@shared/types';
import type {
  AdminCreateInvitesResponse, AdminInvite, AdminInvitesRequest, AdminLoginResponse, AdminUser, AudioPayload, AuthResponse,
  CardEditResponse, CardIdResponse, ChatRequest, PlanEditRequest, DebugBody, DebugResponse, DebugTool, DeviceRequest, DoneResponse, FirstHelpRequest,
  FirstHelpResponse, FriendAction, GoneResponse, InviteCheckResponse, LaterResponse, MessagesResponse, NotifyAction,
  NotifyActionResponse, OkResponse, ScheduleResponse, SettingsPatch, SplitRequest, SplitResponse, StateOkResponse,
  StateResponse, StuckActionResponse, StuckChatResponse, StuckResponse, SttHint, SttPublicRequest, SttResponse, TempPasswordResponse, UnsplitResponse,
  WeekAnswerRequest, WeekAnswerResponse, WeekChestResponse,
} from './types';
import type { ClientErrorsRequest, FeedbackRequest } from '@shared/types';
import { getToken } from './token';
import { platform } from '../native';
import { readSSE } from './sse';
import { crumb } from '../lib/trail';

export class ApiError extends Error {
  constructor(public code: string, message: string, public status = 0) { super(message); this.name = 'ApiError'; }
  /** No response at all (offline, DNS, CORS, aborted). */
  get offline() { return this.status === 0; }
}

export type Transport = (url: string, init: RequestInit) => Promise<Response>;
let transport: Transport = (url, init) => fetch(url, init);
/** Replace the network layer (mock backend, tests). */
export function setTransport(t: Transport) { transport = t; }

/** Calls left out of the feedback trail (the reports themselves). */
const QUIET = /^\/api\/(feedback|client-errors)\b/;

export const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') || '';

export function timeZone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}

function headers(json: boolean, extra?: Record<string, string>): Record<string, string> {
  const h: Record<string, string> = { 'X-TZ': timeZone(), 'X-Client': platform(), Accept: 'application/json', ...extra };
  if (json) h['Content-Type'] = 'application/json';
  const t = getToken();
  if (t && !h.Authorization) h.Authorization = 'Bearer ' + t;
  return h;
}

/** Listeners for 401s (the store signs out). */
const unauthorized = new Set<() => void>();
export function onUnauthorized(fn: () => void) { unauthorized.add(fn); return () => { unauthorized.delete(fn); }; }

async function raw(method: string, path: string, body?: unknown, opts: { signal?: AbortSignal; headers?: Record<string, string>; accept?: string } = {}): Promise<Response> {
  let res: Response;
  try {
    res = await transport(API_BASE + path, {
      method,
      headers: headers(body !== undefined, { ...(opts.accept ? { Accept: opts.accept } : {}), ...opts.headers }),
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: opts.signal,
      credentials: 'omit',
    });
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw new ApiError('aborted', 'Cancelled', 0);
    if (!QUIET.test(path)) crumb(`api ${method} ${path} -> offline`);
    throw new ApiError('network', "Can't reach Xiaoman. Check your connection.", 0);
  }
  if (!res.ok) {
    let code = 'http_' + res.status, message = res.statusText || 'Something went wrong.';
    try {
      const j = await res.json();
      if (j && j.error) { code = j.error.code || code; message = j.error.message || message; }
    } catch { /* not JSON */ }
    if (!QUIET.test(path)) crumb(`api ${method} ${path} -> ${res.status} ${code}`);
    if (res.status === 401) unauthorized.forEach((fn) => fn());
    throw new ApiError(code, message, res.status);
  }
  if (method !== 'GET' && !QUIET.test(path)) crumb(`api ${method} ${path} -> ${res.status}`);
  return res;
}

export async function request<T>(method: string, path: string, body?: unknown, opts?: { signal?: AbortSignal; headers?: Record<string, string> }): Promise<T> {
  const res = await raw(method, path, body, opts);
  if (res.status === 204) return undefined as T;
  try { return (await res.json()) as T; } catch { throw new ApiError('bad_json', 'The server sent something unexpected.', res.status); }
}

const enc = encodeURIComponent;
const post = <T>(path: string, body: unknown = {}, signal?: AbortSignal) => request<T>('POST', path, body, { signal });

/** POST /api/chat: streams ChatEvent over SSE; calls onEvent for each and resolves with the final state ('end'). */
export async function chat(body: ChatRequest, onEvent: (e: ChatEvent) => void, signal?: AbortSignal): Promise<AppState | null> {
  const res = await raw('POST', '/api/chat', body, { signal, accept: 'text/event-stream' });
  let last: AppState | null = null;
  const type = res.headers.get('Content-Type') || '';
  if (!/event-stream/.test(type)) {
    // Non-streaming fallback: { events: ChatEvent[] } or { state }.
    const j = await res.json().catch(() => null) as { events?: ChatEvent[]; state?: AppState } | null;
    for (const e of j?.events || []) { onEvent(e); if (e.t === 'end') last = e.state; }
    return last || j?.state || null;
  }
  for await (const ev of readSSE(res, signal)) {
    let e: ChatEvent;
    try { e = JSON.parse(ev.data) as ChatEvent; } catch { continue; }
    onEvent(e);
    if (e.t === 'end') last = e.state;
  }
  return last;
}

export const api = {
  // ---------------------------------------------------------- onboarding (no auth)
  inviteCheck: (code: string) => post<InviteCheckResponse>('/api/invite/check', { code }),
  firstHelp: (body: FirstHelpRequest, signal?: AbortSignal) => post<FirstHelpResponse>('/api/onboarding/first-help', body, signal),
  sttPublic: (body: SttPublicRequest, signal?: AbortSignal) => post<SttResponse>('/api/stt/public', body, signal),
  register: (body: RegisterRequest) => post<AuthResponse>('/api/auth/register', body),
  login: (email: string, password: string) => post<AuthResponse>('/api/auth/login', { email, password }),
  logout: () => post<OkResponse>('/api/auth/logout'),
  /** Sign out on every device (bumps the session epoch; open sockets close with 4001). */
  logoutAll: () => post<OkResponse>('/api/auth/logout-all'),
  deleteAccount: () => request<GoneResponse>('DELETE', '/api/account'),

  // ---------------------------------------------------------- state, chat, voice
  state: (signal?: AbortSignal) => request<StateResponse>('GET', '/api/state', undefined, { signal }),
  chat,
  stt: (audio: AudioPayload, hint?: SttHint, signal?: AbortSignal) => post<SttResponse>('/api/stt', hint ? { audio, hint } : { audio }, signal),
  /** Older chat history of the current buddy (oldest first), before message `before`. */
  messages: (before?: string | null, limit = 60) => request<MessagesResponse>('GET', `/api/messages?limit=${limit}${before ? '&before=' + enc(before) : ''}`),

  // ---------------------------------------------------------- plan + cards
  planConfirm: (messageId: string) => post<StateResponse>('/api/plan/confirm', { messageId }),
  /** Fix the rows of a plan card before "Looks good" (a misheard word, a wrong time); 409 once it is accepted. */
  planEdit: (messageId: string, cards: PlanEditRequest['cards']) => request<StateResponse>('PATCH', `/api/plan/${enc(messageId)}`, { cards }),
  /** Change a card still to do: title, time ('9:30 AM') and/or minutes. */
  cardEdit: (id: string, edit: CardEdit) => request<CardEditResponse>('PATCH', `/api/cards/${enc(id)}`, edit),
  cardStart: (id: string, alt: string | null = null) => post<StateResponse>(`/api/cards/${enc(id)}/start`, { alt }),
  /** `from`: 'chat' / 'work-chat' when finished from chat (the server adds the 'done' note); none from Today. */
  cardDone: (id: string, from?: DoneFrom) => post<DoneResponse>(`/api/cards/${enc(id)}/done`, from ? { from } : {}),
  /** Undo a light celebration: only the card just finished, today, and not a big one (409 otherwise). */
  cardUndone: (id: string) => post<StateResponse>(`/api/cards/${enc(id)}/undone`),
  /** Stuck in chat: no reason = her ask with the six reasons; a reason = her help. */
  cardStuckChat: (id: string, reason?: StuckReason) => post<StuckChatResponse>(`/api/cards/${enc(id)}/stuck-chat`, reason ? { reason } : {}),
  stuckAction: (messageId: string, a: StuckAction) => post<StuckActionResponse>(`/api/stuck/${enc(messageId)}`, a),
  cardRemove: (id: string) => post<StateResponse>(`/api/cards/${enc(id)}/remove`),
  cardRestore: (id: string) => post<StateResponse>(`/api/cards/${enc(id)}/restore`),
  cardLater: (id: string, when: LaterWhen) => post<LaterResponse>(`/api/cards/${enc(id)}/later`, { when }),
  cardStuck: (id: string, reason: StuckReason) => post<StuckResponse>(`/api/cards/${enc(id)}/stuck`, { reason }),
  cardSplit: (id: string, cards: SplitRequest['cards']) => post<SplitResponse>(`/api/cards/${enc(id)}/split`, { cards }),
  cardUnsplit: (id: string) => post<UnsplitResponse>(`/api/cards/${enc(id)}/unsplit`),
  changeUndo: (changeId: string) => post<StateResponse>(`/api/changes/${enc(changeId)}/undo`),
  nudgeAccept: (messageId: string) => post<CardIdResponse>('/api/nudge/accept', { messageId }),
  offer: (messageId: string, accept: boolean) => post<CardIdResponse>(`/api/offer/${enc(messageId)}`, { accept }),
  goodnight: () => post<StateResponse>('/api/day/goodnight'),
  wrap: () => post<StateResponse>('/api/day/wrap'),
  rewardSeen: () => post<StateResponse>('/api/reward/seen'),

  // ---------------------------------------------------------- us
  weekAnswer: (body: WeekAnswerRequest) => post<WeekAnswerResponse>('/api/week/answer', body),
  weekChest: () => post<WeekChestResponse>('/api/week/chest'),
  storyChoice: (chap: number, choice: string) => post<StateResponse>(`/api/story/${chap}/choice`, { choice }),
  /** Clears `buddy.newChapter`. */
  storyRead: (chap: number) => post<StateResponse>(`/api/story/${chap}/read`),

  // ---------------------------------------------------------- me
  memoryEdit: (id: string, text: string) => request<StateResponse>('PATCH', `/api/memory/${enc(id)}`, { text }),
  memoryDelete: (id: string) => request<StateResponse>('DELETE', `/api/memory/${enc(id)}`),
  memoryForgetAll: () => request<StateResponse>('DELETE', '/api/memory'),
  settings: (patch: SettingsPatch) => request<StateResponse>('PATCH', '/api/settings', patch),
  buddySwitch: (ch: AppState['ch']) => post<StateResponse>('/api/buddy/switch', { ch }),
  vip: (on: boolean) => post<StateResponse>('/api/vip', { on }),
  friend: (id: string, action: FriendAction) => post<StateOkResponse>(`/api/friends/${enc(id)}/${action}`),
  friendsShare: () => post<StateOkResponse>('/api/friends/share'),

  // ---------------------------------------------------------- devices + notifications
  devices: (body: DeviceRequest) => post<OkResponse>('/api/devices', body),
  schedule: () => request<ScheduleResponse>('GET', '/api/schedule'),
  notifyAction: (noteId: string, action: NotifyAction) => post<NotifyActionResponse>('/api/notify/action', { noteId, action }),

  // ---------------------------------------------------------- feedback (β button; works with or without an account)
  feedback: (body: FeedbackRequest) => post<{ ok: true; id: string }>('/api/feedback', body),
  clientErrors: (body: ClientErrorsRequest) => post<{ ok: true; saved: number }>('/api/client-errors', body),

  // ---------------------------------------------------------- test tools (internal build)
  debug: (tool: Exclude<DebugTool, 'restart'>, body: DebugBody = {}) => post<DebugResponse>(`/api/debug/${tool}`, body),
  /** Deletes the account and refunds the invite use (testers re-run onboarding). No state comes back. */
  debugRestart: () => post<GoneResponse>('/api/debug/restart'),
};

/** Admin endpoints (the /admin page; not used by the app screens). Admin token is separate from the user token. */
export const adminApi = {
  login: (password: string) => post<AdminLoginResponse>('/api/admin/login', { password }),
  users: (adminToken: string) => request<AdminUser[]>('GET', '/api/admin/users', undefined, { headers: { Authorization: 'Bearer ' + adminToken } }),
  createInvites: (adminToken: string, body: AdminInvitesRequest) => request<AdminCreateInvitesResponse>('POST', '/api/admin/invites', body, { headers: { Authorization: 'Bearer ' + adminToken } }),
  invites: (adminToken: string) => request<{ invites: AdminInvite[] }>('GET', '/api/admin/invites', undefined, { headers: { Authorization: 'Bearer ' + adminToken } }),
  resetUser: (adminToken: string, id: string) => request<TempPasswordResponse>('POST', `/api/admin/users/${enc(id)}/reset`, {}, { headers: { Authorization: 'Bearer ' + adminToken } }),
  deleteUser: (adminToken: string, id: string) => request<OkResponse>('DELETE', `/api/admin/users/${enc(id)}`, undefined, { headers: { Authorization: 'Bearer ' + adminToken } }),
};

export type Api = typeof api;
